import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { IEntity } from '@utils/utils';
import { plainToClass } from 'class-transformer';
import { LocalStorageService } from '@services/localStorage/local-storage.service';
import { SessionManagerService } from '@services/sessionManager/session-manager.service';

interface IHttpOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

export class ApiService {
  protected baseUrlApi: string;

  constructor(protected http: HttpClient, protected localStorageService: LocalStorageService) {}

  private processResponse<T>(response: Observable<T>, responseType: IEntity<T>) {
    return response.pipe(
      map((data) => plainToClass(responseType, data, { excludeExtraneousValues: true }))
    );
  }

  private responseInterceptorMapper = <T>(response: HttpResponse<T>) => {
    const headers: HttpHeaders = response.headers;
    const renewedBearer = headers.get('Renewed-Bearer');

    if (renewedBearer) {
      // replace current bearer token with a renewed one
      this.localStorageService.apiToken = renewedBearer;
    }

    return response.body;
  };

  private getInterceptor<T>(url: string, options?: IHttpOptions) {
    SessionManagerService.INSTANCE.resetInactivity();

    return this.http
      .get<T>(url, { ...options, observe: 'response' })
      .pipe(map(this.responseInterceptorMapper));
  }

  private postInterceptor<T>(url: string, body: any | null, options?: IHttpOptions) {
    SessionManagerService.INSTANCE.resetInactivity();

    return this.http
      .post<T>(url, body, { ...options, observe: 'response' })
      .pipe(map(this.responseInterceptorMapper));
  }

  private putInterceptor<T>(url: string, body: any | null, options?: IHttpOptions) {
    SessionManagerService.INSTANCE.resetInactivity();

    return this.http
      .put<T>(url, body, { ...options, observe: 'response' })
      .pipe(map(this.responseInterceptorMapper));
  }

  private deleteInterceptor<T>(url: string, options?: IHttpOptions) {
    SessionManagerService.INSTANCE.resetInactivity();

    return this.http
      .delete<T>(url, { ...options, observe: 'response' })
      .pipe(map(this.responseInterceptorMapper));
  }

  public getFile(endpoint: string) {
    const url = this.baseUrlApi + endpoint;

    return this.http.get<Blob>(url, { observe: 'response', responseType: 'blob' as 'json' });
  }

  public postNGetFile(endpoint: string, body: any) {
    const url = this.baseUrlApi + endpoint;

    return this.http.post<Blob>(url, body, { observe: 'response', responseType: 'blob' as 'json' });
  }

  public get<T>(endpoint: string, responseType?: IEntity<T>, options?: IHttpOptions) {
    const url = this.baseUrlApi + endpoint;

    if (responseType) {
      return this.processResponse(this.getInterceptor<T>(url, options), responseType);
    }

    return this.getInterceptor<T>(url, options);
  }

  public post<T>(endpoint: string, body: any, responseType?: IEntity<T>, options?: IHttpOptions) {
    const url = this.baseUrlApi + endpoint;

    if (responseType) {
      return this.processResponse(this.postInterceptor<T>(url, body, options), responseType);
    }

    return this.postInterceptor<T>(url, body, options);
  }

  public put<T>(endpoint: string, body: any, responseType?: IEntity<T>, options?: IHttpOptions) {
    const url = this.baseUrlApi + endpoint;

    if (responseType) {
      return this.processResponse(this.putInterceptor<T>(url, body, options), responseType);
    }

    return this.putInterceptor<T>(url, body, options);
  }

  public delete<T>(endpoint: string, responseType?: IEntity<T>, options?: IHttpOptions) {
    const url = this.baseUrlApi + endpoint;

    if (responseType) {
      return this.processResponse(this.deleteInterceptor<T>(url, options), responseType);
    }

    return this.deleteInterceptor<T>(url, options);
  }
}
